/**
 * Utilitários de Validação de Documentos Brasileiros
 * CPF e CNPJ
 */

/**
 * Valida um CPF usando o algoritmo de verificação de dígitos
 * @param cpf - CPF com ou sem formatação
 * @returns true se o CPF é válido, false caso contrário
 */
export const validateCPF = (cpf: string): boolean => {
  // Remove formatação
  const cleanCPF = cpf.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos
  if (cleanCPF.length !== 11) {
    return false;
  }
  
  // Verifica se todos os dígitos são iguais (ex: 111.111.111-11)
  if (/^(\d)\1{10}$/.test(cleanCPF)) {
    return false;
  }
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let remainder = 11 - (sum % 11);
  let firstDigit = remainder >= 10 ? 0 : remainder;
  
  if (firstDigit !== parseInt(cleanCPF.charAt(9))) {
    return false;
  }
  
  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  remainder = 11 - (sum % 11);
  let secondDigit = remainder >= 10 ? 0 : remainder;
  
  if (secondDigit !== parseInt(cleanCPF.charAt(10))) {
    return false;
  }
  
  return true;
};

/**
 * Valida um CNPJ usando o algoritmo de verificação de dígitos
 * @param cnpj - CNPJ com ou sem formatação
 * @returns true se o CNPJ é válido, false caso contrário
 */
export const validateCNPJ = (cnpj: string): boolean => {
  // Remove formatação
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  
  // Verifica se tem 14 dígitos
  if (cleanCNPJ.length !== 14) {
    return false;
  }
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(cleanCNPJ)) {
    return false;
  }
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let remainder = sum % 11;
  let firstDigit = remainder < 2 ? 0 : 11 - remainder;
  
  if (firstDigit !== parseInt(cleanCNPJ.charAt(12))) {
    return false;
  }
  
  // Validação do segundo dígito verificador
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  remainder = sum % 11;
  let secondDigit = remainder < 2 ? 0 : 11 - remainder;
  
  if (secondDigit !== parseInt(cleanCNPJ.charAt(13))) {
    return false;
  }
  
  return true;
};

/**
 * Valida um email
 * @param email - Email a ser validado
 * @returns true se o email é válido, false caso contrário
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * DDDs válidos no plano de numeração brasileiro (ANATEL).
 * Usado para rejeitar códigos de área inexistentes antes de enviar ao Shopify,
 * que valida o número com a libphonenumber e recusa DDDs inválidos.
 */
const DDDS_VALIDOS = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

/**
 * Valida um telefone brasileiro seguindo as mesmas regras que o Shopify aplica.
 * Aceita fixo (10 dígitos) e celular (11 dígitos), exigindo:
 * - DDD existente;
 * - celular com o 9º dígito (3º após o DDD) igual a 9;
 * - fixo com o 3º dígito entre 2 e 5;
 * - não pode ser uma sequência de dígitos repetidos.
 * @param phone - Telefone com ou sem formatação
 * @returns true se o telefone é válido, false caso contrário
 */
export const validatePhone = (phone: string): boolean => {
  const cleanPhone = phone.replace(/\D/g, '');

  // Deve ter 10 (fixo) ou 11 (celular) dígitos: DDD + número
  if (cleanPhone.length !== 10 && cleanPhone.length !== 11) {
    return false;
  }

  // Rejeita dígitos todos iguais (ex: (11) 11111-1111)
  if (/^(\d)\1+$/.test(cleanPhone)) {
    return false;
  }

  // DDD precisa existir
  const ddd = parseInt(cleanPhone.slice(0, 2), 10);
  if (!DDDS_VALIDOS.has(ddd)) {
    return false;
  }

  const firstDigit = cleanPhone.charAt(2);

  if (cleanPhone.length === 11) {
    // Celular: primeiro dígito do assinante deve ser 9
    return firstDigit === '9';
  }

  // Fixo: primeiro dígito do assinante entre 2 e 5
  return firstDigit >= '2' && firstDigit <= '5';
};

/**
 * Valida um CEP brasileiro
 * @param cep - CEP com ou sem formatação
 * @returns true se o CEP é válido, false caso contrário
 */
export const validateCEP = (cep: string): boolean => {
  const cleanCEP = cep.replace(/\D/g, '');
  return cleanCEP.length === 8;
};

/**
 * Formata um CPF
 * @param value - CPF sem formatação ou parcialmente formatado
 * @returns CPF formatado (000.000.000-00)
 */
export const formatCPF = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 11) {
    return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return cleanValue.slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formata um CNPJ
 * @param value - CNPJ sem formatação ou parcialmente formatado
 * @returns CNPJ formatado (00.000.000/0000-00)
 */
export const formatCNPJ = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 14) {
    return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cleanValue.slice(0, 14).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Formata um telefone
 * @param value - Telefone sem formatação ou parcialmente formatado
 * @returns Telefone formatado ((00) 00000-0000 ou (00) 0000-0000)
 */
export const formatPhone = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 11) {
    if (cleanValue.length <= 10) {
      return cleanValue.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return cleanValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return cleanValue.slice(0, 11).replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

/**
 * Formata um CEP
 * @param value - CEP sem formatação ou parcialmente formatado
 * @returns CEP formatado (00000-000)
 */
export const formatCEP = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 8) {
    return cleanValue.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  return cleanValue.slice(0, 8).replace(/(\d{5})(\d{3})/, '$1-$2');
};
